import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStoreSettings } from '../../context/StoreSettingsContext';
import { useCompanyWallet } from '../../context/CompanyWalletContext';
import { WalletTransaction, WalletTransactionType, WalletTransactionStatus } from '../../types/stores';
import MinimumOrderCard from './MinimumOrderCard';
import DeliveryZonesList from '../../context/DeliveryZonesList';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const StoreSettingsPanel: React.FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { storeSettings, deliveryZones, operationalHours, wallet } = useStoreSettings();
  const { 
    walletBalance, 
    recentTransactions, 
    walletStats, 
    fetchTransactions,
    isLoadingTransactions 
  } = useCompanyWallet();
  
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // منع التمرير عندما يكون اللوحة مفتوحة
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      loadTransactions();
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // دالة لجلب المعاملات
  const loadTransactions = useCallback(async () => {
    if (isOpen) {
      await fetchTransactions();
    }
  }, [isOpen, fetchTransactions]);

  const isStoreOpen = () => {
    if (!operationalHours) return false;
    const now = new Date();
    const day = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const todayHours = operationalHours.find(h => h.day === day);
    if (!todayHours || !todayHours.isOpen) return false;
    const [oh, om] = todayHours.openTime.split(':').map(Number);
    const [ch, cm] = todayHours.closeTime.split(':').map(Number);
    return currentTime >= oh * 60 + om && currentTime <= ch * 60 + cm;
  };

  // دالة للتعامل مع التنقل
  const handleNavigation = (path: string) => {
    onClose();
    navigate(path);
  };

  // إغلاق اللوحة عند الضغط على زر الهروب
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // حساب إحصائيات إضافية من المعاملات
  const calculateAdditionalStats = () => {
    if (!recentTransactions || recentTransactions.length === 0) {
      return {
        todayRevenue: 0,
        weeklyRevenue: 0,
        totalTransactions: 0,
        averageTransaction: 0,
        successfulTransactions: 0,
        failedTransactions: 0
      };
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    let todayRevenue = 0;
    let weeklyRevenue = 0;
    let successfulTransactions = 0;
    let failedTransactions = 0;
    let totalAmount = 0;

    recentTransactions.forEach(tx => {
      const txDate = new Date(tx.created_at);
      const amount = parseFloat(tx.amount);
      
      if (tx.type === 'collect' && tx.status === 'completed') {
        totalAmount += amount;
        
        if (txDate >= today) {
          todayRevenue += amount;
        }
        
        if (txDate >= oneWeekAgo) {
          weeklyRevenue += amount;
        }
      }

      if (tx.status === 'completed') {
        successfulTransactions++;
      } else if (tx.status === 'failed') {
        failedTransactions++;
      }
    });

    return {
      todayRevenue,
      weeklyRevenue,
      totalTransactions: recentTransactions.length,
      averageTransaction: recentTransactions.length > 0 ? totalAmount / recentTransactions.length : 0,
      successfulTransactions,
      failedTransactions
    };
  };

  const additionalStats = calculateAdditionalStats();

  // SVG Icons للإحصائيات
  const StatsIcons = {
    revenue: () => (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    transactions: () => (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
    success: () => (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    average: () => (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    trend: () => (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    )
  };

  // SVG Icons الأساسية
  const CloseIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  const StoreIcon = () => (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );

  const WalletIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );

  const DeliveryIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
    </svg>
  );

  const PaymentIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );

  const ChevronDown = () => (
    <svg className="w-5 h-5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );

  const ClockIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const LocationIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );

  const ArrowRightIcon = () => (
    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
    </svg>
  );

  const CreditCardIcon = () => (
    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );

  // دالة لتحويل نوع المعاملة إلى نص مفهوم
  const getTransactionTypeText = (type: WalletTransactionType): string => {
    const typeMap: Record<WalletTransactionType, string> = {
      'send': t('transactionTypes.send', { defaultValue: 'Send' }),
      'collect': t('transactionTypes.collect', { defaultValue: 'Collect' }),
      'transfer': t('transactionTypes.transfer', { defaultValue: 'Transfer' }),
      'withdraw': t('transactionTypes.withdraw', { defaultValue: 'Withdraw' }),
      'refund': t('transactionTypes.refund', { defaultValue: 'Refund' })
    };
    return typeMap[type] || type;
  };

  // دالة لتحويل حالة المعاملة إلى لون
  const getStatusColor = (status: WalletTransactionStatus): string => {
    const colorMap: Record<WalletTransactionStatus, string> = {
      'pending': 'text-yellow-600',
      'completed': 'text-green-600',
      'failed': 'text-red-600',
      'cancelled': 'text-gray-600'
    };
    return colorMap[status] || 'text-gray-600';
  };

  // دالة لمعرفة إذا كانت المعاملة إضافة (credit) أو خصم (debit)
  const isCreditTransaction = (type: WalletTransactionType): boolean => {
    return type === 'collect' || type === 'refund';
  };

  // دالة الحصول على ساعات اليوم الحالي
  const getTodayHours = () => {
    if (!operationalHours) return { openTime: '9:00', closeTime: '22:00' };
    const day = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const today = operationalHours.find(h => h.day === day);
    return today ? { openTime: today.openTime, closeTime: today.closeTime } : { openTime: '9:00', closeTime: '22:00' };
  };

  const todayHours = getTodayHours();

  // حساب الرصيد من المحفظة إذا كان موجوداً
  const displayBalance = wallet?.balance ? parseFloat(wallet.balance) : walletBalance;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 overflow-y-auto"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-100 z-10">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl shadow-sm">
                        <StoreIcon />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">
                          {t('StoreSettingsPanel.header.title')}
                        </h2>
                        <div className="flex items-center space-x-2 mt-1">
                          <div className={`w-3 h-3 rounded-full ${isStoreOpen() ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                          <span className={`text-sm font-medium ${isStoreOpen() ? 'text-green-600' : 'text-red-600'}`}>
                            {isStoreOpen()
                              ? t('StoreSettingsPanel.header.open')
                              : t('StoreSettingsPanel.header.closed')}
                          </span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={onClose}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-200 hover:scale-110 active:scale-95"
                      aria-label="Close panel"
                    >
                      <CloseIcon />
                    </button>
                  </div>

                  {/* Store Status Badge */}
                  <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 text-sm font-medium border border-blue-100">
                    <ClockIcon />
                    <span className="ml-2 font-semibold">
                      {todayHours.openTime} - {todayHours.closeTime}
                    </span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 p-6 space-y-6 overflow-y-auto">
                {/* Wallet Section مع إحصائيات مصغرة */}
                <motion.div 
                  className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl overflow-hidden border border-blue-100 shadow-lg"
                  whileHover={{ scale: 1.01 }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                >
                  <button
                    onClick={() => toggleSection('wallet')}
                    className="w-full p-5 flex items-center justify-between group"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-white rounded-xl shadow-sm">
                        <WalletIcon />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-900 text-lg">
                          {t('StoreSettingsPanel.wallet.title')}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {t('StoreSettingsPanel.wallet.balance')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                        ${displayBalance?.toFixed(2) || '0.00'}
                      </span>
                      <div className={`transform transition-transform duration-300 ${expandedSection === 'wallet' ? 'rotate-180' : ''}`}>
                        <ChevronDown />
                      </div>
                    </div>
                  </button>

                  {/* إحصائيات مصغرة (Mini Stats) - تظهر دائمًا */}
                  <div className="px-5 pb-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {/* إيرادات اليوم */}
                      <div className="bg-white/80 p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="p-1 bg-green-100 rounded">
                              <StatsIcons.revenue />
                            </div>
                            <span className="text-xs text-gray-600">اليوم</span>
                          </div>
                          <span className="text-sm font-bold text-green-600">
                            ${additionalStats.todayRevenue.toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* إجمالي المعاملات */}
                      <div className="bg-white/80 p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="p-1 bg-blue-100 rounded">
                              <StatsIcons.transactions />
                            </div>
                            <span className="text-xs text-gray-600">المعاملات</span>
                          </div>
                          <span className="text-sm font-bold text-blue-600">
                            {additionalStats.totalTransactions}
                          </span>
                        </div>
                      </div>

                      {/* معدل النجاح */}
                      <div className="bg-white/80 p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="p-1 bg-purple-100 rounded">
                              <StatsIcons.success />
                            </div>
                            <span className="text-xs text-gray-600">الناجحة</span>
                          </div>
                          <span className="text-sm font-bold text-purple-600">
                            {additionalStats.totalTransactions > 0 
                              ? `${((additionalStats.successfulTransactions / additionalStats.totalTransactions) * 100).toFixed(0)}%`
                              : '0%'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <AnimatePresence>
                    {expandedSection === 'wallet' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-5 space-y-4">
                          {/* إحصائيات إضافية موسعة */}
                          <div className="grid grid-cols-2 gap-3">
                            {/* إيرادات الأسبوع */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-lg border border-blue-100">
                              <div className="flex items-center space-x-2 mb-1">
                                <div className="p-1 bg-blue-100 rounded">
                                  <StatsIcons.trend />
                                </div>
                                <span className="text-xs text-gray-600">الأسبوع</span>
                              </div>
                              <p className="text-lg font-bold text-blue-600">
                                ${additionalStats.weeklyRevenue.toFixed(2)}
                              </p>
                            </div>

                            {/* متوسط المعاملة */}
                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-100">
                              <div className="flex items-center space-x-2 mb-1">
                                <div className="p-1 bg-green-100 rounded">
                                  <StatsIcons.average />
                                </div>
                                <span className="text-xs text-gray-600">المتوسط</span>
                              </div>
                              <p className="text-lg font-bold text-green-600">
                                ${additionalStats.averageTransaction.toFixed(2)}
                              </p>
                            </div>
                          </div>

                          {/* إحصائيات من walletStats */}
                          {walletStats && (
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-white/80 p-3 rounded-lg">
                                <p className="text-xs text-gray-500">إجمالي الإيرادات</p>
                                <p className="text-lg font-bold text-green-600">
                                  ${walletStats.totalRevenue.toFixed(2)}
                                </p>
                              </div>
                              <div className="bg-white/80 p-3 rounded-lg">
                                <p className="text-xs text-gray-500">المعلقة</p>
                                <p className="text-lg font-bold text-yellow-600">
                                  ${walletStats.pendingBalance.toFixed(2)}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Recent Transactions */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-gray-700">
                                {t('StoreSettingsPanel.wallet.recent')}
                              </h4>
                              {isLoadingTransactions && (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                              )}
                            </div>
                            <div className="space-y-3 max-h-48 overflow-y-auto">
                              {recentTransactions.length === 0 ? (
                                <p className="text-center text-gray-500 text-sm py-4">
                                  {t('noTransactions')}
                                </p>
                              ) : (
                                recentTransactions.slice(0, 3).map((tx: WalletTransaction, i: number) => (
                                  <motion.div
                                    key={tx.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                    className="flex items-center justify-between p-3 bg-white/80 backdrop-blur-sm rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <p className="font-medium text-gray-900 truncate">
                                          {getTransactionTypeText(tx.type)}
                                        </p>
                                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(tx.status)} bg-opacity-20`}>
                                          {tx.status}
                                        </span>
                                      </div>
                                      <p className="text-xs text-gray-500 truncate">
                                        {tx.note || tx.type || tx.reference_id || ''}
                                      </p>
                                      <p className="text-xs text-gray-400 mt-1">
                                        {new Date(tx.created_at).toLocaleDateString()}
                                      </p>
                                    </div>
                                    <div className="text-right ml-3">
                                      <span className={`font-semibold ${isCreditTransaction(tx.type) ? 'text-green-600' : 'text-red-600'}`}>
                                        {isCreditTransaction(tx.type) ? '+' : '-'}${parseFloat(tx.amount).toFixed(2)}
                                      </span>
                                      {parseFloat(tx.fee) > 0 && (
                                        <p className="text-xs text-gray-500">
                                          Fee: ${parseFloat(tx.fee).toFixed(2)}
                                        </p>
                                      )}
                                    </div>
                                  </motion.div>
                                ))
                              )}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="grid grid-cols-2 gap-3">
                            <motion.button
                              onClick={() => handleNavigation('/admin/wallet')}
                              className="py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium flex items-center justify-center hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow hover:shadow-md active:scale-95"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <WalletIcon />
                              <span className="ml-2 text-sm">{t('StoreSettingsPanel.wallet.manage')}</span>
                            </motion.button>

                            <motion.button
                              onClick={() => handleNavigation('/admin/wallet')}
                              className="py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg font-medium flex items-center justify-center hover:from-gray-700 hover:to-gray-800 transition-all duration-200 shadow hover:shadow-md active:scale-95"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <ArrowRightIcon />
                              <span className="ml-2 text-sm">{t('viewAll')}</span>
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Delivery Section */}
                <motion.div 
                  className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl overflow-hidden border border-green-100 shadow-lg"
                  whileHover={{ scale: 1.01 }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                >
                  <button
                    onClick={() => toggleSection('delivery')}
                    className="w-full p-5 flex items-center justify-between group"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-white rounded-xl shadow-sm">
                        <DeliveryIcon />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-900 text-lg">
                          {t('StoreSettingsPanel.delivery.title')}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {t('StoreSettingsPanel.delivery.settings')}
                        </p>
                      </div>
                    </div>
                    <div className={`transform transition-transform duration-300 ${expandedSection === 'delivery' ? 'rotate-180' : ''}`}>
                      <ChevronDown />
                    </div>
                  </button>

                  <AnimatePresence>
                    {expandedSection === 'delivery' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-5 space-y-4">
                          {/* Delivery Zones */}
             {deliveryZones && 
             
             <DeliveryZonesList deliveryZones={deliveryZones} t={t} />
             }  


                <MinimumOrderCard storeSettings={storeSettings} t={t} />


                          {/* Configure Button */}
                          <motion.button
                            onClick={() => handleNavigation('/admin/delivery-settings')}
                            className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-medium flex items-center justify-center hover:from-green-700 hover:to-emerald-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-lg hover:shadow-xl active:scale-95"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <DeliveryIcon />
                            <span className="ml-2">{t('StoreSettingsPanel.delivery.configure')}</span>
                            <ArrowRightIcon />
                          </motion.button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Payment Methods */}
                {storeSettings?.paymentMethods && storeSettings.paymentMethods.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-5 border border-purple-100 shadow-lg"
                  >
                    <div className="flex items-center mb-4">
                      <div className="p-3 bg-white rounded-xl shadow-sm mr-3">
                        <PaymentIcon />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">
                          {t('StoreSettingsPanel.payment.methods')}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {t('StoreSettingsPanel.payment.accepted')}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {storeSettings.paymentMethods.map((method, i) => (
                        <motion.span
                          key={i}
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: i * 0.05 }}
                          whileHover={{ scale: 1.05, y: -2 }}
                          className="inline-flex items-center px-4 py-2 bg-white rounded-full text-sm font-medium text-gray-700 shadow-sm hover:shadow-md transition-all duration-200"
                        >
                          <CreditCardIcon />
                          {method}
                        </motion.span>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-gradient-to-t from-white via-white to-transparent pt-6 pb-8 px-6 border-t border-gray-100">
                <p className="text-center text-sm text-gray-500">
                  {t('StoreSettingsPanel.footer.note')}
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default StoreSettingsPanel;